import React from 'react';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import { ScheduledPayment } from '../types/models';
import { createScheduledPayment } from '../api/scheduledPayments';
import { TWAP_DEFAULT_MIN, TWAP_DEFAULT_MAX, TWAP_DEFAULT_INTERVAL } from '../constants/twap';

interface ScheduledPaymentFormProps {
  payment?: ScheduledPayment | null; // unused for now (future edit)
  initial?: Partial<ScheduledPayment> | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function ScheduledPaymentForm({ payment, initial, onSuccess, onCancel }: ScheduledPaymentFormProps) {
  const nowLocal = React.useMemo(() => new Date(Date.now() + new Date().getTimezoneOffset() * -60000)
    .toISOString().slice(0,16), []);

  // Support pre-population via controlled prop
  const effectiveInitial = initial || null;
  const [source, setSource] = React.useState(effectiveInitial?.source_account_id || '');
  const [target, setTarget] = React.useState(effectiveInitial?.target_account_id || '');
  const [amount, setAmount] = React.useState(effectiveInitial?.amount || '');
  const [scheduledFor, setScheduledFor] = React.useState<string>(() => {
    if (effectiveInitial?.scheduled_for) {
      try {
        const d = new Date(effectiveInitial.scheduled_for as any);
        return new Date(d.getTime() - d.getTimezoneOffset()*60000).toISOString().slice(0,16);
      } catch { /* noop */ }
    }
    return nowLocal;
  });
  const [saving, setSaving] = React.useState(false);
  const [useTwap, setUseTwap] = React.useState(initial?.use_twap || false);
  const [twapMin, setTwapMin] = React.useState(
    effectiveInitial?.twap_payment?.slice_min_amount ?? TWAP_DEFAULT_MIN
  );
  const [twapMax, setTwapMax] = React.useState(
    effectiveInitial?.twap_payment?.slice_max_amount ?? TWAP_DEFAULT_MAX
  );
  const [interval, setInterval] = React.useState(
    effectiveInitial?.twap_payment?.slice_interval_seconds ?? TWAP_DEFAULT_INTERVAL
  );

  // Add an effect to default interval to 3600 when TWAP is set true the first time
  React.useEffect(() => {
    if (useTwap && !interval) setInterval(TWAP_DEFAULT_INTERVAL);
    if (!useTwap) setInterval('');
  }, [useTwap]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const base = {
        source_account_id: source,
        target_account_id: target,
        amount: String(amount),
        scheduled_for: new Date(scheduledFor).toISOString(),
        status: 'scheduled',
        use_twap: useTwap,
      } as any;
      if (useTwap) {
        base.twap_payment_attributes = {
          amount_total: String(amount),
          slice_min_amount: twapMin,
          slice_max_amount: twapMax,
          slice_interval_seconds: interval,
        };
      }
      await createScheduledPayment(base);
      onSuccess && onSuccess();
    } catch (err: any) {
      alert(err.message || 'Failed to schedule payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label="Source Account"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          fullWidth
          required
        />
        <TextField
          label="Target Account"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          fullWidth
          required
        />
        <TextField
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          inputProps={{ step: '0.0000001', min: '0' }}
          fullWidth
          required
        />
        <TextField
          label="Scheduled For"
          type="datetime-local"
          value={scheduledFor}
          onChange={(e) => setScheduledFor(e.target.value)}
          fullWidth
          required
        />
      </Box>
      {/* TWAP Section */}
      <Box sx={{ mt: 2 }}>
        <label>
          <input
            type="checkbox"
            checked={useTwap}
            onChange={e => setUseTwap(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Deliver as time-sliced payment (TWAP)
        </label>
        {useTwap && (
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            <TextField
              label="Min Slice Amount"
              value={twapMin}
              onChange={(e) => setTwapMin(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: '0.0000001' }}
              fullWidth
              required
            />
            <TextField
              label="Max Slice Amount"
              value={twapMax}
              onChange={(e) => setTwapMax(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: '0.0000001' }}
              fullWidth
              required
            />
            <TextField
              label="Slice Interval (seconds)"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              type="number"
              inputProps={{ min: 1, step: '1' }}
              fullWidth
              required
            />
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </Box>
    </form>
  );
}